import { formatMinorPlain } from '../Shared/Formatters';

/**
 * Column config for the invoice register — drives both the on-screen table and
 * the client-side export.
 *
 * MONEY USES formatMinorPlain, NEVER formatMoney. `formatMoney` abbreviates
 * above 1000 ("AED 1.28M"), which is meaningless in a spreadsheet someone
 * reconciles against a bank statement. Values arrive from the API as integer
 * minor units, so everything here divides by 100 exactly once.
 */

export const INVOICE_COLUMNS = [
    { key: 'invoice_number', label: 'Invoice #', get: (i) => i.invoice_number || '—' },
    { key: 'status', label: 'Status', get: (i) => i.status },
    { key: 'store_name', label: 'Tenant', get: (i) => i.bill_to?.store_name || '—' },
    { key: 'tenant_code', label: 'Tenant Code', get: (i) => i.bill_to?.tenant_code || '—' },
    { key: 'unit_code', label: 'Unit', get: (i) => i.bill_to?.unit_code || '—' },
    { key: 'property_name', label: 'Property', get: (i) => i.bill_to?.property_name || '—' },
    { key: 'period_key', label: 'Period', get: (i) => i.period_key },
    { key: 'invoice_date', label: 'Invoice Date', get: (i) => dateOnly(i.invoice_date) },
    { key: 'due_date', label: 'Due Date', get: (i) => dateOnly(i.due_date) },
    { key: 'billable_days', label: 'Billable Days', numeric: true, get: (i) => i.billable_days },
    { key: 'currency', label: 'Currency', get: (i) => i.currency },
    { key: 'subtotal', label: 'Subtotal', numeric: true, money: true, get: (i) => formatMinorPlain(i.subtotal_minor) },
    { key: 'tax', label: 'Tax', numeric: true, money: true, get: (i) => formatMinorPlain(i.tax_total_minor) },
    { key: 'total', label: 'Total', numeric: true, money: true, get: (i) => formatMinorPlain(i.total_minor) },
    { key: 'is_holdover', label: 'Holdover', get: (i) => (i.is_holdover ? 'Yes' : 'No') },
    { key: 'lease_expired_on', label: 'Lease Expired On', get: (i) => i.lease_expired_on || '' },
    {
        key: 'variance',
        label: 'Variance vs Revenue Share Report',
        numeric: true,
        money: true,
        get: (i) => formatMinorPlain(i.reconciliation?.variance_minor || 0),
    },
];

/** Columns shown on screen by default — the export carries the full set. */
export const DEFAULT_VISIBLE = [
    'invoice_number', 'status', 'store_name', 'unit_code', 'period_key',
    'billable_days', 'total', 'due_date',
];

function dateOnly(d) {
    if (!d) return '';
    // Slice the ISO string rather than constructing a Date: `new Date(iso)`
    // then reading local parts shifts the day across a timezone boundary, which
    // on an invoice date is an off-by-one someone will query.
    return String(d).slice(0, 10);
}

/** Build a CSV from the full column set. Client-side sibling of the API export. */
export function toCsv(rows) {
    const cell = (v) => {
        const s = v === null || v === undefined ? '' : String(v);
        // A store name starting = + - @ is executed as a formula by Excel.
        const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
        return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
    };
    const header = INVOICE_COLUMNS.map((c) => cell(c.label)).join(',');
    const body = rows.map((r) => INVOICE_COLUMNS.map((c) => cell(c.get(r))).join(','));
    return `﻿${[header, ...body].join('\r\n')}`;
}

export default INVOICE_COLUMNS;
